import { type QueryClient, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { AppConfig, StrongerMemoryTypes } from '../config/environment';
import { loadMarks, type SmMark, withMarks } from '../content/marks';
import { queryKeys } from '../query/keys';
import { useConfig, useSession } from './provider';
import { useLocalSessions } from './use-local-sessions';
import { useMe } from './use-me';

function marksQuery(types: StrongerMemoryTypes, groupActorUuid: string, memberActorUuid: string) {
  return {
    queryKey: queryKeys.marks(memberActorUuid),
    staleTime: 60_000,
    queryFn: (): Promise<SmMark[]> => loadMarks(types, groupActorUuid, memberActorUuid),
  };
}

/**
 * The marks leaders made for the signed-in person (`content/marks.ts`). Off where the
 * environment has no group or no content types.
 */
export function useMarks() {
  const session = useSession();
  const { strongerMemoryTypes: types, strongerMemoryGroupActorUuid: groupActorUuid } = useConfig();
  const meUuid = useMe().data?.uuid;
  return useQuery({
    ...marksQuery(types as StrongerMemoryTypes, groupActorUuid ?? 'none', meUuid ?? 'none'),
    enabled:
      session.status === 'authenticated' &&
      types !== undefined &&
      groupActorUuid !== undefined &&
      meUuid !== undefined,
  });
}

/**
 * The sessions every count reads: the person's own, with the marks leaders made for them
 * (`withMarks`) and the exercises waiting in the outbox (`useLocalSessions`). Counts go on
 * without marks while they load, or when they can't be read. Saving an exercise reads
 * `useSessions`: nothing is ever written into a mark's or a queued attempt's row.
 */
export function useCountedSessions() {
  const sessions = useLocalSessions();
  const marks = useMarks();
  const data = useMemo(
    () => (sessions.data ? withMarks([...sessions.data], marks.data ?? []) : undefined),
    [sessions.data, marks.data],
  );
  return {
    data,
    isLoading: sessions.isLoading,
    isError: sessions.isError,
    error: sessions.error,
    refetch: sessions.refetch,
  };
}

/**
 * The marks a summary about to be written counts: the cached ones while fresh, read otherwise.
 * None where they can't be read, or there is no one to read them for: the next summary counts
 * them, and the group's view adds the marks that are done now whatever the summary says.
 */
export async function marksForSummary(
  queryClient: QueryClient,
  config: Pick<AppConfig, 'strongerMemoryTypes' | 'strongerMemoryGroupActorUuid'>,
  memberActorUuid: string | undefined,
): Promise<SmMark[]> {
  const { strongerMemoryTypes: types, strongerMemoryGroupActorUuid: groupActorUuid } = config;
  if (!types || !groupActorUuid || !memberActorUuid) return [];
  try {
    return await queryClient.fetchQuery(marksQuery(types, groupActorUuid, memberActorUuid));
  } catch {
    return [];
  }
}
